import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ContextService } from '../../../core/services/context.service';
import { MsalService } from '../../../core/services/msal.service';
import { SessionService } from '../../../core/services/session.service';
import { OpsNoticesComponent } from '../ops-notices/ops-notices.component';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, OpsNoticesComponent],
  templateUrl: './header.component.html',
})
export class HeaderComponent {
  @Input() isAuthenticated = false;
  @Input() userName: string | undefined;
  @Input() breadcrumb: string | undefined;
  @Input() sidebarCollapsed = false;
  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() onSignIn = new EventEmitter<void>();
  @Output() onSignOut = new EventEmitter<void>();

  private readonly context = inject(ContextService);
  private readonly session = inject(SessionService);
  protected readonly msal = inject(MsalService);
  private clockTimer: ReturnType<typeof setInterval> | undefined;
  @ViewChild('userMenu') private userMenu?: ElementRef<HTMLElement>;

  protected readonly envName = environment.environmentName;
  protected readonly utcTime = signal(this.formatUtcTime());
  protected readonly tenantName = computed(() => {
    const tenantId = this.session.workingTenantId();
    const tenants = this.context.registeredTenants();
    return tenantId && tenants ? tenants[tenantId]?.name : undefined;
  });

  menuOpen = false;

  ngOnInit(): void {
    this.clockTimer = setInterval(() => this.utcTime.set(this.formatUtcTime()), 30_000);
  }

  ngOnDestroy(): void {
    if (this.clockTimer) clearInterval(this.clockTimer);
  }

  get userInitials(): string {
    if (!this.userName) return '?';
    return this.userName
      .split(' ')
      .map(w => w[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  /** Any click outside the user menu closes it, including one on the notifications bell. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.menuOpen && !this.userMenu?.nativeElement.contains(event.target as Node)) {
      this.menuOpen = false;
    }
  }

  private formatUtcTime(): string {
    const now = new Date();
    return `${now.getUTCHours().toString().padStart(2, '0')}:${now.getUTCMinutes().toString().padStart(2, '0')} UTC`;
  }
}
